import { useEffect } from 'react';
import { getStudentData } from '../assets/data';
import { getLessonListForStudent, getLessonsFromLocalStorage, saveLessonsToLocalStorage } from '../utils/lessonsService';
import { getScheduleForStudent } from '../utils/scheduleService';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components).
//
// handleLessonStatusUpdate is the same sensitive auto-charge listener that
// needed the source==='schedule' gate fixed earlier this session (a
// status change from the Lessons tab must never add/remove a charge, only
// one from the schedule does) — moved byte-for-byte identical on purpose.
export function useScheduleSync({
  studentId,
  isNew,
  setStudentLessons,
  studentLessonsRef,
  allStudentScheduleItemsRef,
  setAllStudentScheduleItems,
  setUpcomingLessons,
  paymentsKey,
  paymentsRef,
  studentDataRef,
  computeLessonCharge,
  savePayments,
}) {
  useEffect(() => {
    if (studentId && studentId !== "new" && !isNew) {
      const currentStudent = getStudentData()[studentId];
      const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;

      if (!studentIdForLessons) return;

      const handleScheduleUpdate = () => {
        // Обновляем список уроков при изменении расписания
        getLessonListForStudent(studentIdForLessons).then(lessons => {
          setStudentLessons(lessons);
        }).catch(error => {
          console.error('Error loading lessons after schedule update:', error);
        });
      };

      const handleLessonStatusUpdate = (e) => {
        const { lessonId, status, prevStatus: eventPrevStatus, source } = e.detail || {};
        if (!lessonId) return;

        // Resolve the lesson from either source (lessons table or schedule
        // table) for the title and time fields. prevStatus is preferred
        // from the event payload — by the time the listener runs, the
        // dispatcher may already have committed a state update that
        // flipped the ref's status to the new value, so a ref-only check
        // would falsely report "no change".
        const lesson =
          studentLessonsRef.current.find(l => l.id === lessonId) ||
          allStudentScheduleItemsRef.current.find(s => s.id === lessonId);
        if (!lesson) return;

        const prevStatus = eventPrevStatus !== undefined ? eventPrevStatus : lesson.status;
        const pKey = paymentsKey;

        setStudentLessons(prev => prev.map(l => l.id === lessonId ? { ...l, status } : l));

        // Charging is driven ONLY by the schedule's own status — a status
        // change made from the Lessons tab is just a progress indicator
        // (a single lesson can span several taught sessions) and must
        // never add or remove a charge, even though both tabs dispatch
        // this same event to keep the two lists' displayed status in sync.
        if (pKey && prevStatus !== status && source === 'schedule') {
          // paymentsRef, not loadPayments() — loadPayments() can return a
          // stale snapshot right after a remount (see paymentsRef's own
          // comment above); paymentsRef always mirrors the hook's actual
          // current payments state.
          const existing = paymentsRef.current;
          let next = null;
          if (status === 'done' && prevStatus !== 'done') {
            const alreadyCharged = existing.some(p => p.lessonId === lessonId && p.type === 'charge');
            const priceAmt = parseFloat(studentDataRef.current.priceAmount) || 0;
            if (!alreadyCharged && priceAmt > 0) {
              const amt = computeLessonCharge(lesson, priceAmt);
              next = [...existing, {
                id: Date.now(),
                date: lesson.date || new Date().toISOString(),
                amount: -amt,
                type: 'charge',
                lessonId,
                note: `${lesson.title || 'Lesson'} (auto)`,
              }];
            }
          } else if (status !== 'done' && prevStatus === 'done') {
            // Only write if a charge was actually removed — filtering with
            // no match returns an equal-length array, and writing that
            // unconditionally was overwriting a just-added deposit/charge
            // with a stale `existing` snapshot whenever this fired for a
            // lesson that had no auto-charge to begin with.
            const filtered = existing.filter(p => !(p.lessonId === lessonId && p.type === 'charge'));
            if (filtered.length !== existing.length) next = filtered;
          }
          if (next) {
            savePayments(next);
          }
        }

        const allLessons = getLessonsFromLocalStorage();
        saveLessonsToLocalStorage(
          allLessons.map(l => l.id === lessonId ? { ...l, status } : l)
        );
      };

      window.addEventListener('scheduleUpdated', handleScheduleUpdate);
      window.addEventListener('lessonStatusUpdated', handleLessonStatusUpdate);

      return () => {
        window.removeEventListener('scheduleUpdated', handleScheduleUpdate);
        window.removeEventListener('lessonStatusUpdated', handleLessonStatusUpdate);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, isNew]);

  const updateUpcomingLessons = async () => {
    if (studentId && studentId !== "new" && !isNew) {
      try {
        // Получаем правильный ID студента
        const currentStudent = getStudentData()[studentId];
        const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;

        // Загружаем расписание из Supabase
        const scheduleItems = await getScheduleForStudent(studentIdForLessons);

        console.log('💰 updateUpcomingLessons - loaded schedule items:', {
          studentId,
          studentIdForLessons,
          scheduleItemsCount: scheduleItems.length,
          scheduleItems: scheduleItems.map(item => ({
            id: item.id,
            date: item.date,
            status: item.status,
            studentId: item.studentId,
            type: item.type,
            from: item.from || item.timeFrom,
            to: item.to || item.timeTo
          }))
        });

        // Сохраняем все уроки из расписания для расчета статистики
        setAllStudentScheduleItems(scheduleItems);

        // Также получаем из localStorage как fallback
        const allLessons = getLessonsFromLocalStorage();
        const localStorageLessons = allLessons.filter(lesson => {
          const lessonStudentId = lesson.studentId || '';
          return lessonStudentId === studentId || lessonStudentId === studentIdForLessons;
        });

        // Объединяем данные из Supabase и localStorage (приоритет у Supabase)
        const combinedLessons = [
          ...scheduleItems,
          ...localStorageLessons.filter(lesson =>
            !scheduleItems.some(scheduleItem => scheduleItem.id === lesson.id)
          )
        ];

        // Сохраняем все уроки расписания (включая прошедшие и done) для отображения в недельном календаре
        const withDate = combinedLessons.filter(lesson => !!lesson.date);
        const sortedLessons = withDate.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
          }
          // Если даты одинаковые, сортируем по времени
          const timeA = (a.from || a.timeFrom || '00:00').replace(':', '');
          const timeB = (b.from || b.timeFrom || '00:00').replace(':', '');
          return timeA.localeCompare(timeB);
        });

        // Сохраняем все upcoming lessons, а не только 2, чтобы они отображались в недельном календаре
        setUpcomingLessons(sortedLessons);
      } catch (error) {
        console.error('❌ Error loading upcoming lessons from schedule:', error);
        // Fallback на localStorage если Supabase недоступен
        const allLessons = getLessonsFromLocalStorage();
        const currentStudent = getStudentData()[studentId];
        const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
        const scheduleLessons = allLessons.filter(lesson => {
          const lessonStudentId = lesson.studentId || '';
          return lessonStudentId === studentId || lessonStudentId === studentIdForLessons;
        });

        // Сохраняем все уроки для статистики
        setAllStudentScheduleItems(scheduleLessons);

        const withDate = (scheduleLessons || []).filter(lesson => !!lesson.date);
        const sortedLessons = withDate.sort((a, b) => new Date(a.date) - new Date(b.date));
        // Сохраняем все upcoming lessons для отображения в недельном календаре
        setUpcomingLessons(sortedLessons);
      }
    }
  };

  return { updateUpcomingLessons };
}
